/**
 * FibrationExplainer — progressive-disclosure explainer for fibrations and
 * opfibrations, the categorical structures behind DATA.ORIGIN and DATA.REACH.
 *
 * A fibration π : E → B equips a "total" category with cartesian lifts along
 * the base. When the lift does not exist, the transition is structurally
 * inexpressible — that is the seam. The card uses:
 *
 *   - Shared ModeToggle to flip between "lift exists" and "lift fails" modes
 *   - Shared StepperDemo that walks through the lift construction step by step
 *   - CalloutBox for the DATA.LINEAGE dual-pair guarantee
 *   - Concrete examples (provenance tracking, supply chain)
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { THEOREM_COLOR } from "../../design-system/tokens.js";
import { MathInline, tex } from "../../components/math/index.js";
import {
  CalloutBox,
  ExplainerCard,
  FibrationDiagram,
  ModeToggle,
  StepperDemo,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";

const ACCENT = THEOREM_COLOR["DATA.ORIGIN"];
const REACH_ACCENT = THEOREM_COLOR["DATA.REACH"];

const IDENTITY: ExplainerIdentity = {
  id: "explainer-fibration",
  kind: "Categorical structure",
  title: "Fibration / Opfibration",
  subtitle: "π : E → B with cartesian lifts (or their absence as seams)",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "DATA.ORIGIN (D1)", href: "#explainer-data-origin", accent: ACCENT },
  { label: "DATA.REACH (D2)", href: "#explainer-data-reach", accent: REACH_ACCENT },
  { label: "Beck-Chevalley", href: "#explainer-beck-chevalley" },
  { label: "Adjunction", href: "#explainer-adjunction" },
  { label: "Double category", href: "#explainer-double-category" },
];

// inline: local
const LIFT_STEPS = [
  {
    label: "Start",
    description: "A morphism f : A → B exists in the base category B (a data-flow step).",
  },
  {
    label: "Target fiber",
    description: "We know e_B sits above B in the total category E (data with annotations at B).",
  },
  {
    label: "Lift attempt",
    description: "We ask: is there a cartesian morphism f̃ : e_A → e_B in E with π(f̃) = f?",
  },
  {
    label: "Success → bridge",
    description:
      "If yes, the annotations at A are determined — provenance is structurally tracked.",
  },
  {
    label: "Failure → seam",
    description:
      "If no, no object above A can claim to be the source — the origin is unconstructable.",
  },
] as const;

const TOGGLE_OPTIONS = [
  { mode: "lifts" as const, label: "Lift exists (bridge)" },
  { mode: "fails" as const, label: "Lift fails (seam)" },
] as const;

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      A <strong>fibration</strong> is a functor <MathInline>{tex`\pi : E \to B`}</MathInline>{" "}
      equipped with &ldquo;cartesian lifts&rdquo; — for every morphism in the base{" "}
      <MathInline>{tex`B`}</MathInline> and every object above the target, there is a canonical lift
      in <MathInline>{tex`E`}</MathInline>. CSK uses the <em>failure</em> of this lift as a seam:
      when the cartesian lift does not exist, the transition is structurally inexpressible. This is
      the mechanism behind <strong>DATA.ORIGIN</strong> and <strong>DATA.REACH</strong>.
    </p>
  ),
  intuition: (
    <>
      <p>
        Think of <strong>E</strong> as the &ldquo;annotated world&rdquo; — data enriched with
        provenance tags, classification labels, or ownership certificates. Think of{" "}
        <strong>B</strong> as the &ldquo;stripped world&rdquo; — the same data with all annotations
        forgotten. The functor <MathInline>{tex`\pi`}</MathInline> forgets the annotations.
      </p>
      <p>
        A <strong>cartesian lift</strong> is the ability to pull annotations backward along any base
        morphism. If <MathInline>{tex`f : A \to B`}</MathInline> is a data-flow step in{" "}
        <MathInline>{tex`B`}</MathInline> and you know the annotations at{" "}
        <MathInline>{tex`B`}</MathInline>, the lift reconstructs what the annotations must have been
        at <MathInline>{tex`A`}</MathInline>. When the lift exists, provenance can be reconstructed.
        When it does not, some data flow has lost its origin — that is a <em>seam</em>.
      </p>
      <p>
        <strong>Concrete example:</strong> imagine a supply chain tracking system. Each component
        has an origin certificate. When a component moves from supplier → assembler → distributor,
        the fibration says: &ldquo;given the final product at the distributor, I can reconstruct
        which supplier made each sub-component.&rdquo; If a component arrives without a valid lift
        back to a supplier, it cannot enter the governed category at all — the &ldquo;uncertified
        component in the supply chain&rdquo; type is uninhabited.
      </p>
      <p>
        The dual story is an <strong>opfibration</strong>: instead of pulling annotations backward,
        you push them forward. When the opcartesian lift fails, data cannot reach a scope it has no
        right to touch.
      </p>
      <StepperDemo
        steps={LIFT_STEPS}
        accent={ACCENT}
        title="Lift construction"
        ariaLabel="Cartesian lift stepper demo"
      />
    </>
  ),
  formal: (
    <>
      <p>
        A functor <MathInline>{tex`\pi : E \to B`}</MathInline> is a <strong>fibration</strong>{" "}
        (Grothendieck fibration) when for every morphism <MathInline>{tex`f : a \to b`}</MathInline>{" "}
        in <MathInline>{tex`B`}</MathInline> and every object <MathInline>{tex`e_{b}`}</MathInline>{" "}
        in <MathInline>{tex`E`}</MathInline> with <MathInline>{tex`\pi(e_{b}) = b`}</MathInline>,
        there exists a <strong>cartesian morphism</strong>{" "}
        <MathInline>{tex`\tilde{f} : e_{a} \to e_{b}`}</MathInline> with{" "}
        <MathInline>{tex`\pi(\tilde{f}) = f`}</MathInline>, satisfying the universal property:
      </p>
      <ul>
        <li>
          <strong>Universality.</strong> For any <MathInline>{tex`g : e' \to e_{b}`}</MathInline> in{" "}
          <MathInline>{tex`E`}</MathInline> with <MathInline>{tex`\pi(g) = f \circ h`}</MathInline>,
          there exists a unique <MathInline>{tex`u : e' \to e_{a}`}</MathInline> with{" "}
          <MathInline>{tex`\tilde{f} \circ u = g`}</MathInline> and{" "}
          <MathInline>{tex`\pi(u) = h`}</MathInline>.
        </li>
        <li>
          <strong>Uniqueness up to iso.</strong> Cartesian lifts are unique up to unique isomorphism
          in the fiber.
        </li>
      </ul>
      <ModeToggle
        options={TOGGLE_OPTIONS}
        defaultMode="lifts"
        accent={ACCENT}
        ariaLabel="Switch between cartesian lift exists and lift fails"
        caption={(m) =>
          m === "lifts"
            ? "The cartesian lift f̃ exists — provenance is structurally reconstructable."
            : "The cartesian lift is absent — the origin claim is unconstructable (seam)."
        }
      >
        {(mode) => <FibrationDiagram mode={mode} accent={ACCENT} />}
      </ModeToggle>
      <p style={{ marginTop: "0.75rem" }}>
        Dually, <MathInline>{tex`\pi`}</MathInline> is an <strong>opfibration</strong> when it has{" "}
        <em>opcartesian</em> lifts. The <strong>DATA.LINEAGE</strong> dual pair (D1 + D2) arises
        from the{" "}
        <MathInline ariaLabel="fibration is left adjoint to opfibration">
          {tex`\text{fibration} \dashv \text{opfibration}`}
        </MathInline>{" "}
        adjunction: cartesian reindexing <MathInline>{tex`\dashv`}</MathInline> opcartesian
        pushforward.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        <strong>DATA.ORIGIN</strong> (D1) uses the fibration: the cartesian lift being absent means
        data cannot claim an origin it does not have — structurally inexpressible, not merely
        unverified.
      </p>
      <p>
        <strong>DATA.REACH</strong> (D2) uses the opfibration: the opcartesian lift failing means
        data cannot reach a scope it has no right to touch.
      </p>
      <CalloutBox eyebrow="Dual Pair Guarantee — DATA.LINEAGE" accent={ACCENT}>
        <p style={{ margin: 0 }}>
          Together they form the <strong>DATA.LINEAGE</strong> dual pair (D1 + D2), strictly
          stronger than either alone. The{" "}
          <MathInline ariaLabel="fibration is left adjoint to opfibration">
            {tex`\text{fibration} \dashv \text{opfibration}`}
          </MathInline>{" "}
          adjunction couples restricting provenance and extending scope as complementary operations.
          Deploying DATA.LINEAGE with the TRACE SDP yields the <strong>ICG-Tr</strong> Safety
          Envelope.
        </p>
      </CalloutBox>
      <p style={{ marginTop: "0.75rem" }}>
        Seam classification: DATA.ORIGIN is <em>vertical</em> (IMMUNE); DATA.REACH is{" "}
        <em>horizontal</em> (IMMUNE). Governance artifacts:{" "}
        <strong>declassification certificate</strong> (D1) and <strong>declass witness</strong>{" "}
        (D2).
      </p>
    </>
  ),
  references: [
    {
      label: "Foundation/DoubleCategory/Seams.agda",
      path: "agda/Foundation/DoubleCategory/Seams.agda",
      note: "Mechanized DCSeam, DCBridge, SeamBridgePair definitions.",
    },
    {
      label: "Foundation/Lemmas/Seams.agda",
      path: "agda/Foundation/Lemmas/Seams.agda",
      note: "1-categorical seams and path-forbidding lemmas.",
    },
    {
      label: "Theorem-Canon.md",
      path: "docs/reference/Theorem-Canon.md",
      note: "DATA.ORIGIN, DATA.REACH, DATA.LINEAGE DPG, ICG-Tr.",
    },
    {
      label: "SEAM-THEORY.md",
      path: "theory/structural/SEAM-THEORY.md",
      note: "Full structural theory of seams and BC failures; DATA.ORIGIN/DATA.REACH seam classification.",
    },
    {
      label: "SEAM-ADJUNCTION-BRIDGE-KAN.md",
      path: "theory/structural/SEAM-ADJUNCTION-BRIDGE-KAN.md",
      note: "Restricted distributive bridges over adjunctions.",
    },
  ],
};

export function FibrationExplainer(): React.JSX.Element {
  return (
    <ExplainerCard
      identity={IDENTITY}
      tiers={TIERS}
      diagram={<FibrationDiagram mode="lifts" accent={ACCENT} />}
      crossLinks={CROSS_LINKS}
    />
  );
}
